import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export { default as Button } from './Button.vue';

// Colour variants are cut onto the --btn-* token contract (ADR-0035:
// semantic tokens only in ui/*), replacing the stock shadcn classes — three of
// which were dead (bg-destructive / bg-accent / text-*-foreground resolve to
// nothing; the shadcn @theme block is commented out) and one a false friend
// (bg-primary here is the atNorth app-background token, not a button colour).
//
// Light-mode values are chosen so `default` and `destructive` reproduce the
// legacy btn-primary / btn-danger utilities exactly, keeping the epic inert
// with the dark mode flag off. `secondary` is the legacy charcoal again —
// the 2723 light-surface cut was reversed by QA against deployed
// (ITDEVELOP-3106); hover keeps prod's pale-blue flip via the hover tokens.
//
// The focus outline colour is still the blue-100 primitive: no --focus-ring
// role token exists yet (design gap recorded on ITDEVELOP-3067).
export const buttonVariants = cva(
    // Geometry (radius / weight / padding) lives in the size variants; the TYPE
    // SCALE does not, because deployed had two button boxes and the split falls
    // on the variant: btn-primary carried text-base (37px) while btn-secondary /
    // btn-danger / btn-alt carried no font-size and inherited body (34px). So
    // `size: default` is the 34px box and `variant: default` adds text-base.
    // The shadcn-shaped sizes remain available under explicit names.
    // [&_p]:text-inherit: the base layer's `p { text-primary }` is an
    // element-level rule that beats colour inheritance from the button, so
    // <p> labels would render body-coloured on accent variants without it.
    'inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:border-[var(--border-focus)] focus-visible:outline-2 focus-visible:outline-blue-100 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_p]:text-inherit',
    {
        variants: {
            variant: {
                default:
                    // The span/p px-xs utilities mirror legacy btn-primary's nested
                    // `p, span { @apply px-xs }` so label width is unchanged.
                    //
                    // Disabled is the pale ghost. In LIGHT the tokens alias
                    // --bg-soft/--text-disabled, which is what legacy
                    // btn-primary's own `&:disabled` block rendered, so every
                    // migrated primary keeps its deployed look; DARK diverges,
                    // because that same pair measures 1.56:1 there. Going
                    // through --btn-disabled-ghost-* is what lets the two themes
                    // differ. ITDEVELOP-3191 briefly made this a filled
                    // --btn-disabled-bg: that belongs to one hand-rolled button
                    // (the incident-category Continue, the only place deployed
                    // filled a disabled primary) and not to the variant.
                    //
                    // text-base lives on the VARIANT, not the size: it is what
                    // makes this box 37px, matching legacy btn-primary. The
                    // other variants descend from btn-secondary / btn-danger /
                    // btn-alt, none of which set a font-size — they inherited
                    // body's 14px/1.3 for a 34px box, and putting text-base on
                    // `size: default` silently grew all of them by 3px.
                    'bg-[var(--btn-primary-bg)] text-base text-[var(--text-on-accent)] shadow hover:bg-[var(--btn-primary-hover-bg)] disabled:bg-[var(--btn-disabled-ghost-bg)] disabled:text-[var(--btn-disabled-ghost-fg)] disabled:opacity-100 [&_span]:px-xs [&_p]:px-xs',
                destructive:
                    'bg-[var(--btn-danger-bg)] text-[var(--btn-danger-fg)] shadow-sm hover:bg-[var(--btn-danger-hover-bg)]',
                // note, we have started styling this outline button as if it is an input field (like InputSelect)
                // maybe we need to separate outline button away from input select
                // Hover border rides --border-focus with .input-wrapper:hover
                // (this variant is styled as an input field — see note above),
                // so both hover as one family in dark.
                outline:
                    'border border-input bg-background  hover:text-accent-foreground hover:not-disabled:border-[var(--border-focus)] hover:not-disabled:outline-2 outline-blue-100 disabled:bg-gray-50',
                'outline-error': '', // implemented in default.css
                // hover:text flips with the hover surface (charcoal → pale blue
                // in light, → deep blue in dark), mirroring legacy btn-secondary.
                secondary:
                    'bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-fg)] hover:bg-[var(--btn-secondary-hover-bg)] hover:text-[var(--text-primary)]',
                ghost: 'hover:bg-[var(--btn-secondary-hover-bg)]',
                // Legacy btn-alt: accent outline that fills on hover. Border
                // and label use the dedicated outline ink — the fill token
                // dims in dark and is not canvas-legible there.
                tertiary:
                    'gap-sm border border-[var(--btn-outline-fg)] text-[var(--btn-outline-fg)] hover:bg-[var(--btn-primary-bg)] hover:text-[var(--text-on-accent)]',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                // Portal geometry, deliberately WITHOUT a font-size: the box
                // then inherits body's 14px/1.3 (18.2px leading → 34px), which
                // is exactly what a raw <button>, btn-secondary, btn-danger and
                // btn-alt measured on deployed. `variant: default` adds
                // text-base on top for the 37px btn-primary box. An explicit
                // size below still wins over the variant's text-base, because
                // cva emits size classes after variant classes and cn()'s
                // tailwind-merge keeps the last font-size (pinned by test).
                default: 'py-xs px-sm rounded-sm font-semibold',
                // The pre-batch-1 shadcn default; FilterableMultiSelect pins it
                // explicitly (its outline variant is styled as an input field).
                field: 'h-9 rounded-md px-4 py-2 text-sm font-medium',
                xs: 'h-7 rounded px-2 text-sm font-medium',
                sm: 'h-8 rounded-md px-3 text-xs font-medium',
                lg: 'h-10 rounded-md px-8 text-sm font-medium',
                icon: 'h-9 w-9 rounded-md',
                'icon-sm': 'size-8 rounded-md',
                'icon-lg': 'size-10 rounded-md',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
